import { XMarkIcon } from "@heroicons/react/24/outline";
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import Button from "../common/Button";
import Input from "../common/Input";

interface TrialInvitation {
  id: string;
  email: string;
  officerSeats: number;
  investigatorSeats: number;
  trialDays: number;
  invitedBy: string;
  expiresAt: string;
  createdAt: string;
  agreedAt: string | null;
  acceptedAt: string | null;
}

interface InviteAgencyModalProps {
  modalRef: React.RefObject<HTMLDialogElement>;
  adminEmail: string;
  /** Called after an invitation is accepted-state-relevant change (send/cancel). */
  onChanged?: () => void;
}

const DEFAULT_TRIAL_DAYS = 45;

const statusOf = (i: TrialInvitation): { label: string; className: string } => {
  if (i.acceptedAt) return { label: "Signed up", className: "text-green-600 dark:text-green-400" };
  if (new Date(i.expiresAt) < new Date()) return { label: "Link expired", className: "text-red-600 dark:text-red-400" };
  if (i.agreedAt) return { label: "Agreed, not signed up", className: "text-amber-600 dark:text-amber-400" };
  return { label: "Sent", className: "text-base-content/60" };
};

const InviteAgencyModal: React.FC<InviteAgencyModalProps> = ({
  modalRef,
  adminEmail,
  onChanged,
}) => {
  const [email, setEmail] = useState("");
  const [officers, setOfficers] = useState("10");
  const [investigators, setInvestigators] = useState("0");
  const [days, setDays] = useState(String(DEFAULT_TRIAL_DAYS));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invitations, setInvitations] = useState<TrialInvitation[]>([]);
  const [filter, setFilter] = useState<"pending" | "all">("pending");
  // Invitation awaiting a second click on Cancel (no browser dialogs).
  const [confirmCancelId, setConfirmCancelId] = useState<string | null>(null);
  const headers = { "x-admin-email": adminEmail };

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get("/api/agency-management/trial-invitations", { headers });
      setInvitations(data.invitations || []);
    } catch (err) {
      console.error("Failed to load trial invitations", err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminEmail]);

  // The <dialog> is always mounted, so refresh whenever it is opened.
  useEffect(() => {
    const dialog = modalRef.current;
    if (!dialog) return;
    const observer = new MutationObserver(() => {
      if (dialog.open) load();
    });
    observer.observe(dialog, { attributes: true, attributeFilter: ["open"] });
    return () => observer.disconnect();
  }, [modalRef, load]);

  const reset = () => {
    setEmail("");
    setOfficers("10");
    setInvestigators("0");
    setDays(String(DEFAULT_TRIAL_DAYS));
    setError(null);
    setSubmitting(false);
  };

  const handleClose = () => {
    reset();
    modalRef.current?.close();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError("Enter a valid email");
      return;
    }
    const o = Number(officers);
    const i = Number(investigators);
    const d = Number(days);
    if (!Number.isInteger(o) || o < 0 || !Number.isInteger(i) || i < 0) {
      setError("Seat counts must be whole numbers");
      return;
    }
    if (o + i === 0) {
      setError("Grant at least one seat");
      return;
    }
    if (!Number.isInteger(d) || d < 1) {
      setError("Trial length must be at least 1 day");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        "/api/agency-management/trial-invitations",
        { email: email.trim(), officerSeats: o, investigatorSeats: i, trialDays: d, adminEmail },
        { headers },
      );
      toast.success(`Trial invitation sent to ${email.trim()}`);
      setEmail("");
      setSubmitting(false);
      await load();
      onChanged?.();
    } catch (err: any) {
      setError(err?.response?.data?.error || "Failed to send invitation");
      setSubmitting(false);
    }
  };

  const resend = async (id: string) => {
    try {
      await axios.post(`/api/agency-management/trial-invitations/${id}/resend`, { adminEmail }, { headers });
      toast.success("Invitation resent");
      await load();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Failed to resend");
    }
  };

  const cancel = async (id: string) => {
    setConfirmCancelId(null);
    try {
      await axios.delete(`/api/agency-management/trial-invitations/${id}`, { headers, data: { adminEmail } });
      toast.success("Invitation cancelled");
      await load();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Failed to cancel");
    }
  };

  // Pending = not signed up yet (includes links that have expired, which
  // are flagged and can be resent).
  const pending = invitations.filter((i) => !i.acceptedAt);
  const shown = filter === "pending" ? pending : invitations;

  return (
    <dialog ref={modalRef} className="modal">
      <div className="modal-box w-[95vw] max-w-[95vw]">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-bold text-lg">Invite agency</h3>
          <button onClick={handleClose} className="btn btn-sm btn-circle btn-ghost" type="button">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <p className="text-sm text-gray-600 dark:text-base-content/70 mb-4">
          Emails a link to the trial requirements. When they agree and finish
          signing up, the seats below are added and the trial clock starts, no
          manual grant needed. You become the agency's owner.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <p className="font-semibold pb-1 text-sm">Contact email</p>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="chief@department.gov"
              className="w-full min-w-full"
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <p className="font-semibold pb-1 text-sm">Officers</p>
              <Input type="number" min={0} value={officers} onChange={(e) => setOfficers(e.target.value)} className="w-full min-w-full" />
            </div>
            <div>
              <p className="font-semibold pb-1 text-sm">Investigators</p>
              <Input type="number" min={0} value={investigators} onChange={(e) => setInvestigators(e.target.value)} className="w-full min-w-full" />
            </div>
            <div>
              <p className="font-semibold pb-1 text-sm">Trial days</p>
              <Input type="number" min={1} value={days} onChange={(e) => setDays(e.target.value)} className="w-full min-w-full" />
            </div>
          </div>
          <p className="text-xs text-base-content/60">
            The trial length applies to both officer and investigator seats and
            starts when they finish signing up.
          </p>

          {error && <p className="text-xs text-red-500">{error}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={handleClose} disabled={submitting}>
              Close
            </Button>
            <Button type="submit" variant="primary" disabled={submitting}>
              {submitting ? (
                <>
                  <span className="loading loading-spinner loading-sm"></span>
                  Sending...
                </>
              ) : (
                "Send invitation"
              )}
            </Button>
          </div>
        </form>

        <div className="mt-6">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-semibold text-sm m-0">Sent invitations</h4>
            <div className="join">
              <button
                type="button"
                className={`join-item btn btn-xs ${filter === "pending" ? "btn-active" : ""}`}
                onClick={() => setFilter("pending")}
              >
                Pending ({pending.length})
              </button>
              <button
                type="button"
                className={`join-item btn btn-xs ${filter === "all" ? "btn-active" : ""}`}
                onClick={() => setFilter("all")}
              >
                All ({invitations.length})
              </button>
            </div>
          </div>
          {shown.length === 0 ? (
            <p className="text-sm text-base-content/60 m-0">
              {filter === "pending" ? "No pending invitations." : "No invitations sent yet."}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="table table-sm w-full">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Seats</th>
                    <th>Days</th>
                    <th>Sent by</th>
                    <th>Sent</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {shown.map((inv) => {
                    const st = statusOf(inv);
                    return (
                      <tr key={inv.id}>
                        <td className="whitespace-nowrap">{inv.email}</td>
                        <td className="whitespace-nowrap">
                          {inv.officerSeats} off / {inv.investigatorSeats} inv
                        </td>
                        <td>{inv.trialDays}</td>
                        <td className="text-xs max-w-[10rem] truncate" title={inv.invitedBy}>{inv.invitedBy}</td>
                        <td className="whitespace-nowrap text-xs">
                          {new Date(inv.createdAt).toLocaleDateString()}
                        </td>
                        <td className={`whitespace-nowrap text-xs ${st.className}`}>{st.label}</td>
                        <td className="whitespace-nowrap text-right">
                          {!inv.acceptedAt &&
                            (confirmCancelId === inv.id ? (
                              <>
                                <span className="text-xs mr-1">Cancel this invite?</span>
                                <button type="button" className="btn btn-error btn-xs" onClick={() => cancel(inv.id)}>
                                  Yes, cancel
                                </button>
                                <button type="button" className="btn btn-ghost btn-xs" onClick={() => setConfirmCancelId(null)}>
                                  Keep
                                </button>
                              </>
                            ) : (
                              <>
                                <button type="button" className="btn btn-outline btn-xs mr-1" onClick={() => resend(inv.id)}>
                                  Resend
                                </button>
                                <button type="button" className="btn btn-outline btn-error btn-xs" onClick={() => setConfirmCancelId(inv.id)}>
                                  Cancel invitation
                                </button>
                              </>
                            ))}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      <form method="dialog" className="modal-backdrop">
        <button onClick={handleClose}>close</button>
      </form>
    </dialog>
  );
};

export default InviteAgencyModal;
